<script lang="ts">
	import { clone } from "@bufbuild/protobuf";
	import { Label, Select } from "flowbite-svelte-v1";
	import { getContext, onMount } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { PageSchema } from "#gen/pages/v2/page_pb.js";
	import P from "#lib/components/P.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import TextAppearanceEditor from "#lib/components/flow/editor/appearance/TextAppearanceEditor.svelte";
	import { TEXT_ELEMENTS, type TextElement } from "#lib/components/flow/editor/appearance/constants.js";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import EditorInset from "#lib/components/page/editing/EditorInset.svelte";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import type { JsonObject } from "#lib/protobuf-compat.js";
	import { sentenceCase } from "#lib/string.js";
	import { setStringValue, setStructValue, stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	const provider: Writable<TokenProvider> = getContext("tokenProvider");

	let { id, page, onChange }: ComponentEditingProps = $props();

	let initiallyUnset = $state(false);

	const text = $derived(stringValue(structValue($page.config, id), "text"));
	const className = $derived(stringValue(structValue($page.config, id), "class"));
	const textElement = $derived(stringValue(structValue($page.config, id), "element") ?? TEXT_ELEMENTS[0].value) as TextElement;

	function getConfig(): JsonObject {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		return config;
	}

	function handleChangeTextElement(e: Event) {
		const value = (e.target as HTMLSelectElement).value as TextElement;
		const config = getConfig();
		setStringValue(config, "element", value);
		if (config.textColor) delete config.textColor;
		$page = $page;
		onChange?.();
	}

	onMount(() => {
		initiallyUnset = stringValue(structValue($page.config, id), "text") === undefined;
	});

	export function validate(): boolean {
		return true;
	}

	function update(e: CustomEvent<string | undefined>) {
		const config = getConfig();
		if (!e.detail && initiallyUnset) {
			delete config.text;
		} else {
			setStringValue(config, "text", e.detail ?? "");
		}
		$page = clone(PageSchema, $page);
	}

	function handleChange(e: CustomEvent<string | undefined>) {
		onChange?.(e.detail);
	}
</script>

<EditorInset>
	<div class={twMerge("flex flex-col gap-6 px-6", className)}>
		<FeatureFlagGate flag="build_your_own_page">
			<div class="flex w-full flex-col gap-5 rounded-lg border p-3">
				<P class="text-sm font-bold text-black">Attributes</P>
				<div class="flex flex-col gap-2">
					<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Text</P>
					<div class="flex w-full gap-2 border-l-[5px] border-l-gray-300 pl-6">
						<Select class="w-48" classes={{ select: "truncate pr-8" }} items={TEXT_ELEMENTS} value={textElement} aria-labelledby="{id}-element-label" onchange={e => handleChangeTextElement(e)} />
						<PlaintextEditor class="grow" provider={$provider} value={text} on:input={update} on:change={handleChange} aria-labelledby="{id}-value-label" />
					</div>
				</div>
			</div>
			<div class="flex w-full flex-col gap-2 rounded-lg border p-3">
				<TextAppearanceEditor {id} {page} {onChange} defaultElement={TEXT_ELEMENTS[0].value} />
			</div>
			{#snippet fallback()}
				<Label id="{id}-label" class="text-md font-bold text-gray-900">{sentenceCase(id)}</Label>
				<PlaintextEditor provider={$provider} value={text} on:input={update} on:change={handleChange} aria-labelledby="{id}-label" />
			{/snippet}
		</FeatureFlagGate>
	</div>
</EditorInset>
